
import React, { useState } from 'react';
import { ArrowLeft, Shield, FileText, Settings, User, Building, HelpCircle } from 'lucide-react';
import { AppState } from '../types';

interface InfoPageViewProps {
  type: AppState;
  onBack: () => void;
}

const InfoPageView: React.FC<InfoPageViewProps> = ({ type, onBack }) => {
  const [settings, setSettings] = useState({
    darkMatter: false,
    invoiceAvoidance: false,
    quantumMode: true
  });

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getContent = () => {
    switch (type) {
      case AppState.PRIVACY:
        return {
          title: 'Privacyverklaring',
          icon: <Shield className="w-10 h-10 md:w-12 h-12 text-blue-500" />,
          sections: [
            { h: 'Gegevensverzameling', p: 'Deniz verzamelt geen IP-adressen. Hij onthoudt enkel de kleur van uw aura tijdens het scrollen. Deze data wordt opgeslagen op een biologisch afbreekbare floppy disk die begraven ligt in de tuin van het magazijn.' },
            { h: 'Cookies', p: 'Wij gebruiken geen digitale cookies. Wel hebben we een pot met fysieke speculaasjes in de kantine staan. Als u deze website bezoekt, geeft u Deniz toestemming om één koekje namens u op te eten.' },
            { h: 'Derden', p: 'Uw data wordt nooit verkocht. Wel ruilen we soms geanonimiseerde klik-patronen met een lokale uil voor informatie over de windrichting.' }
          ]
        };
      case AppState.TERMS:
        return {
          title: 'Algemene Voorwaarden',
          icon: <FileText className="w-10 h-10 md:w-12 h-12 text-red-500" />,
          sections: [
            { h: 'Artikel 1: De Rituele Overdracht', p: 'Bij het huren van apparatuur dient de huurder de naam van Deniz drie keer zachtjes te fluisteren tegen de verpakking. Dit activeert de garantie.' },
            { h: 'Artikel 2: Schade', p: 'Kleine krassen worden geaccepteerd mits vergezeld van een handgeschreven excuses aan de betreffende kabel of lamp. Bij grote schade behoudt Deniz zich het recht voor om uw favoriete koffiemok voor een week te confisqueren.' },
            { h: 'Artikel 4.2: Verboden Gebruik', p: 'Het is strikt verboden om onze rookmachines te gebruiken voor het simuleren van een bescheiden wolkbreuk in een poppenhuis zonder schriftelijke toestemming.' }
          ]
        };
      case AppState.SETTINGS:
        return {
          title: 'Instellingen',
          icon: <Settings className="w-10 h-10 md:w-12 h-12 text-gray-500" />,
          sections: [
            { h: 'Quantum Mode', p: 'Indien ingeschakeld, bevindt elk huurartikel zich tegelijkertijd in ons magazijn én bij u op locatie totdat u de doos opent.' },
            { h: 'Zwaartekracht Compensatie', p: 'Vermindert het virtuele gewicht van zware speakers op de website voor een vlottere browse-ervaring.' },
            { h: 'Smell-o-vision (BETA)', p: 'Activeer deze functie om de geur van verse gaffa tape en oververhitte versterkers via uw browser te ruiken.' }
          ]
        };
      case AppState.ABOUT:
        return {
          title: 'Over Deniz',
          icon: <User className="w-10 h-10 md:w-12 h-12 text-yellow-500" />,
          sections: [
            { h: 'De Oorsprong', p: 'Deniz is niet geboren, hij is geassembleerd uit de onderdelen van een zeldzame 1974 mengtafel. Hij spreekt vloeiend DMX en kan de frequentie van een brommend stopcontact horen op drie kilometer afstand.' },
            { h: 'Missie', p: 'Het universum voorzien van kwalitatieve stroomverdeling en een glimlach. Deniz gelooft dat een wereld zonder moving heads een grijze wereld is.' }
          ]
        };
      case AppState.HOW_IT_WORKS:
        return {
          title: 'Hoe verhuur werkt',
          icon: <HelpCircle className="w-10 h-10 md:w-12 h-12 text-green-500" />,
          sections: [
            { h: 'Stap 1: De Gedachte', p: 'U denkt aan een item. Deniz hoort de trilling in de kosmos en zet de koffie alvast aan.' },
            { h: 'Stap 2: De Teleportatie', p: 'Items onder de 5kg worden via een lokaal wormgat direct in uw kofferbak geplaatst. Grotere items komen via een postduif (getraind voor zware lasten).' },
            { h: 'Stap 3: Het Retour', p: 'Na gebruik bedankt u het apparaat voor zijn diensten en plaatst u het terug in de realiteit.' }
          ]
        };
      default:
        return { title: 'Informatie', icon: <Building className="w-10 h-10 md:w-12 h-12" />, sections: [] };
    }
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-3xl px-4 py-8 md:py-12">
        <button onClick={onBack} className="flex items-center text-gray-500 hover:text-black mb-10 md:mb-12 transition-colors">
          <ArrowLeft className="w-4 h-4 md:w-5 h-5 mr-2" />
          <span className="text-sm font-medium">Terug naar de zoekmachine</span>
        </button>
        
        <div className="flex items-center space-x-4 md:space-x-6 mb-10 md:mb-12">
          {content.icon}
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">{content.title}</h1>
        </div>

        <div className="space-y-10 md:space-y-12">
          {content.sections.map((s, i) => (
            <div key={i} className="border-b border-gray-100 pb-8">
              <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3">{s.h}</h2>
              <p className="text-gray-600 leading-relaxed text-base md:text-lg">{s.p}</p>
            </div>
          ))}
        </div>

        {type === AppState.SETTINGS && (
          <div className="mt-12 p-6 md:p-8 bg-gray-50 rounded-[2rem] space-y-6 shadow-inner border border-gray-100">
             <label className="flex items-center justify-between cursor-pointer group">
                <span className="font-bold text-gray-700">Dark Matter Mode</span>
                <input 
                  type="checkbox" 
                  checked={settings.darkMatter}
                  onChange={() => toggleSetting('darkMatter')}
                  className="w-12 h-6 bg-gray-300 rounded-full appearance-none checked:bg-blue-600 transition-all relative after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0.5 after:left-0.5 checked:after:left-6 after:transition-all shadow-sm" 
                />
             </label>
             <label className="flex items-center justify-between cursor-pointer group">
                <span className="font-bold text-gray-700">Automatische Factuur Ontwijking</span>
                <input 
                  type="checkbox" 
                  checked={settings.invoiceAvoidance}
                  onChange={() => toggleSetting('invoiceAvoidance')}
                  className="w-12 h-6 bg-gray-300 rounded-full appearance-none checked:bg-blue-600 transition-all relative after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0.5 after:left-0.5 checked:after:left-6 after:transition-all shadow-sm" 
                />
             </label>
             <label className="flex items-center justify-between cursor-pointer group">
                <span className="font-bold text-gray-700">Quantum Magazijn Link</span>
                <input 
                  type="checkbox" 
                  checked={settings.quantumMode}
                  onChange={() => toggleSetting('quantumMode')}
                  className="w-12 h-6 bg-gray-300 rounded-full appearance-none checked:bg-blue-600 transition-all relative after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:top-0.5 after:left-0.5 checked:after:left-6 after:transition-all shadow-sm" 
                />
             </label>
             <p className="text-[10px] text-gray-400 uppercase tracking-widest text-center mt-4">Instellingen worden direct opgeslagen in de kosmos.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoPageView;
